import { TAX_REFRESH_TOOLTIP } from './credit-card-form-validation';
import { formatCentsToUsd } from '@/utils/currency';
import { estimatedSendsFromCents, formatSendRatePerThousand } from '@/features/billing-usage';
import { type CreditsPurchaseConfig, remainingPurchaseHeadroom } from './credits-purchase-config';

/**
 * The largest quantity the account may buy given an optional current balance. Uncapped configs (AI
 * credits) always allow `maxQuantity`; Builder credits are limited by the remaining balance headroom.
 */
export function effectiveMaxQuantity(config: CreditsPurchaseConfig, currentBalanceCents: number | undefined): number {
  if (currentBalanceCents == null) {
    return config.maxQuantity;
  }
  return remainingPurchaseHeadroom(config, currentBalanceCents);
}

/**
 * The account balance cap formatted for display, or `undefined` when the config is uncapped or the
 * account isn't actually balance-limited (headroom still equals the config max).
 */
export function balanceLimitLabel(config: CreditsPurchaseConfig, effectiveMax: number): string | undefined {
  const isBalanceLimited = effectiveMax < config.maxQuantity;
  if (config.accountBalanceLimitCents == null || !isBalanceLimited) {
    return undefined;
  }
  return formatCentsToUsd(config.accountBalanceLimitCents, 0);
}

/**
 * The validation message for the quantity field, or `undefined` when valid: an impossible range (cap below the minimum) and over/under bounds each get
 * distinct copy, with the balance cap appended when one applies.
 */
export function quantityError(
  quantity: number,
  config: CreditsPurchaseConfig,
  effectiveMax: number,
  limitLabel: string | undefined,
): string | undefined {
  const { minQuantity, quantityErrorLabel } = config;

  if (effectiveMax < minQuantity && limitLabel != null) {
    return `Account balance cannot exceed ${limitLabel}.`;
  }
  if (quantity < minQuantity) {
    return `Minimum ${quantityErrorLabel} is ${minQuantity}.`;
  }
  if (quantity > effectiveMax) {
    if (limitLabel != null) {
      return `Account balance cannot exceed ${limitLabel}. Maximum ${quantityErrorLabel} is ${effectiveMax}.`;
    }
    return `Maximum ${quantityErrorLabel} is ${effectiveMax}.`;
  }
  return undefined;
}

export type CreditsPurchaseTotals = {
  totalPriceCents: number;
  taxAmountCents: number;
  totalWithTaxCents: number;
  totalCredits: number;
};

/** The price, tax, and credit totals for a quantity, given the tax rate fraction (0 when unknown). */
export function computeTotals(
  quantity: number,
  config: CreditsPurchaseConfig,
  taxRateFraction: number | undefined,
): CreditsPurchaseTotals {
  // Pack pricing is non-linear ($25/100 but $500/5,000), so a pack-priced
  // config takes the selected pack's price rather than quantity × unit price.
  const pack = config.packs?.find((candidate) => candidate.size === quantity);
  const totalPriceCents = pack == null ? quantity * config.pricePerUnitCents : pack.priceCents;
  const taxAmountCents = taxRateFraction == null ? 0 : Math.round(totalPriceCents * taxRateFraction);
  return {
    totalPriceCents,
    taxAmountCents,
    totalWithTaxCents: totalPriceCents + taxAmountCents,
    totalCredits: pack == null ? quantity * (config.creditsPerUnit ?? 0) : pack.size,
  };
}

/**
 * The credit count to report in the purchase funnel, or `undefined` for a config that does not express
 * one. Builder credits are bought in dollars and carry no credit count; the unit-priced and
 * pack-priced configs both do, and `computeTotals` has already resolved it either way.
 *
 * Shared so the Stripe and Ordway rails report the same field for the same product — Inbox Previews
 * is sold on both, and a rail-dependent payload would split its funnel.
 */
export function reportedTotalCredits(config: CreditsPurchaseConfig, totals: CreditsPurchaseTotals): number | undefined {
  const expressesCredits = config.creditsPerUnit != null || config.packs != null;
  return expressesCredits ? totals.totalCredits : undefined;
}

/** The estimated Builder message sends for a total, or `undefined` for non-Builder credit types. */
export function estimatedSends(
  config: CreditsPurchaseConfig,
  totalPriceCents: number,
  costPerMessage: number,
): number | undefined {
  if (config.creditType !== 'builder_credits') {
    return undefined;
  }
  return estimatedSendsFromCents(totalPriceCents, costPerMessage);
}

/**
 * The drawdown rate to print under the Builder disclaimer, or `undefined` for a rail that quotes no
 * rate. Reads the rate the account is charged rather than a literal, so the modal cannot disagree
 * with the balance row that opened it.
 */
export function disclaimerRateNote(config: CreditsPurchaseConfig, costPerMessage: number): string | undefined {
  if (config.creditType !== 'builder_credits') {
    return undefined;
  }
  return `Rate: ${formatSendRatePerThousand(costPerMessage)}.`;
}

/**
 * What to do when Stripe.js cannot be fetched. An ad blocker is the usual cause, so the copy names it
 * — a generic failure leaves the user with nothing to try.
 */
export const STRIPE_LOAD_ERROR_MESSAGE =
  'Unable to load the payment form. Disable any ad blockers and refresh the page.';

/** Which payment-method control the form shows for the current Stripe-load and saved-card state. */
export type CreditsPurchaseCardView = 'stripe-error' | 'saved-card' | 'stripe-loading' | 'new-card';

/**
 * Which payment-method control to render. A failed Stripe.js load wins outright — the fields it backs
 * can never mount — then the saved card, then nothing at all while Stripe.js is still in flight.
 */
export function creditsPurchaseCardView(args: {
  isStripeLoadError: boolean;
  isStripeLoading: boolean;
  useSavedCard: boolean;
}): CreditsPurchaseCardView {
  if (args.isStripeLoadError) {
    return 'stripe-error';
  }
  if (args.useSavedCard) {
    return 'saved-card';
  }
  if (args.isStripeLoading) {
    return 'stripe-loading';
  }
  return 'new-card';
}

/**
 * Whether to collect a billing address. Only a new card needs one, and the fields stay mounted while
 * Stripe.js loads so the address the user has already typed survives the card fields appearing.
 */
export function showsBillingAddress(view: CreditsPurchaseCardView): boolean {
  return view === 'new-card' || view === 'stripe-loading';
}

/**
 * The tooltip explaining why the submit button is disabled, or `''` when there's nothing to explain.
 * A failed payment-form load comes first, because nothing else can be fixed until it loads; then a
 * field error, the quantity error, and incomplete card details.
 *
 * The tax message comes last, and so appears only when the form is otherwise submittable: it is the
 * one block the user cannot act on and that clears by itself, so anything they must fix wins over it.
 */
export function submitTooltipMessage(args: {
  fieldError: string | undefined;
  quantityError: string | undefined;
  allComplete: boolean;
  isStripeLoadError?: boolean;
  /** Whether a tax-rate lookup is in flight; submit is held from the first keystroke until it lands. */
  isTaxRateLoading?: boolean;
}): string {
  if (args.isStripeLoadError === true) {
    return STRIPE_LOAD_ERROR_MESSAGE;
  }
  if (args.fieldError != null) {
    return args.fieldError;
  }
  if (args.quantityError != null) {
    return args.quantityError;
  }
  if (!args.allComplete) {
    return 'Your card details are incomplete.';
  }
  if (args.isTaxRateLoading === true) {
    return TAX_REFRESH_TOOLTIP;
  }
  return '';
}
